import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { EmptyStateComponent } from "src/app/_core/components/empty-state/empty-state.component";
import {
	CupsViewModel,
	EnergiaCatalogos,
} from "src/app/_core/models/energia.model";
import { EnergiaService } from "src/app/_core/providers/energia.service";
import { DialogService } from "src/app/_core/services/dialog.service";
import { CupsDialogComponent, CupsDialogResult } from "./cups-dialog.component";

/**
 * Pestaña "Energía" del detalle de centro: puntos de suministro (CUPS) de la
 * instalación con sus datos contractuales y el consumo previsto. Siempre visible
 * (ver = visibilidad del centro); las acciones de escritura las gatea el padre vía
 * `canManage` (App.Energia.Manage O edicionCentro) y el back las revalida.
 * Standalone, patrón app-historial.
 */
@Component({
	selector: "app-energia-tab",
	standalone: true,
	imports: [
		CommonModule,
		MatIconModule,
		MatTooltipModule,
		TranslateModule,
		EmptyStateComponent,
	],
	templateUrl: "./energia-tab.component.html",
	styleUrls: ["./energia-tab.component.scss"],
})
export class EnergiaTabComponent implements OnChanges {
	@Input() centroId?: number;
	@Input() centroNombre = "";
	/** Escritura: App.Energia.Manage O edición del centro (lo computa el padre). */
	@Input() canManage = false;
	/** Estado de actividad del centro (para la advertencia "activo sin suministro en vigor"). */
	@Input() centroActivo = false;

	public cups: CupsViewModel[] = [];
	public loading = false;
	public catalogos: EnergiaCatalogos = { estados: [], tarifas: [], lotes: [] };

	constructor(
		private readonly energiaService: EnergiaService,
		private readonly dialog: MatDialog,
		private readonly dialogService: DialogService,
		private readonly translate: TranslateService,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["centroId"]) {
			this.cargar();
		}
	}

	private cargar(): void {
		if (!this.centroId || this.centroId <= 0) {
			this.cups = [];
			return;
		}
		this.loading = true;
		this.energiaService.getCupsByCentro(this.centroId).subscribe({
			next: (cups) => {
				this.cups = cups ?? [];
				this.loading = false;
			},
			error: () => {
				this.cups = [];
				this.loading = false;
			},
		});
	}

	// ── KPIs del centro ──

	public get totalKwh(): number {
		return this.cups.reduce((acc, c) => acc + (c.consumoPrevistoKwhAnio ?? 0), 0);
	}

	public get totalPotenciaKw(): number {
		return this.cups.reduce(
			(acc, c) =>
				acc +
				(c.potenciaP1 ?? 0) + (c.potenciaP2 ?? 0) + (c.potenciaP3 ?? 0) +
				(c.potenciaP4 ?? 0) + (c.potenciaP5 ?? 0) + (c.potenciaP6 ?? 0),
			0,
		);
	}

	public get proximoVencimiento(): string | null {
		const fechas = this.cups
			.map((c) => c.fechaFinContrato)
			.filter((f): f is string => !!f)
			.sort();
		return fechas[0] ?? null;
	}

	public formateaKwh(valor: number): string {
		return new Intl.NumberFormat(this.translate.currentLang === "en" ? "en-GB" : "es-ES").format(valor);
	}

	/** ¿El contrato de SUMINISTRO del punto está expirado respecto a hoy? */
	public estaVencido(c: CupsViewModel): boolean {
		return !!c.fechaFinContrato && c.fechaFinContrato.slice(0, 10) < new Date().toISOString().slice(0, 10);
	}

	/**
	 * Advertencia: el centro está ACTIVO pero ningún CUPS está "EN VIGOR" con fecha de
	 * fin de contrato vigente (>= hoy). Cubre también el caso sin CUPS. Fecha nula no
	 * cuenta como vigente (contrato sin fin conocido = pendiente de cumplimentar).
	 */
	public get sinSuministroVigente(): boolean {
		if (!this.centroActivo || this.loading) {
			return false;
		}
		return !this.cups.some(
			(c) => c.estado === "EN VIGOR" && !!c.fechaFinContrato && !this.estaVencido(c),
		);
	}

	/** Nombre visible del punto: alias o, si no hay, el propio centro. */
	public nombrePunto(c: CupsViewModel): string {
		return c.denominacion || this.centroNombre || c.centroDescripcion || "";
	}

	// ── Acciones (gateadas por canManage; el back revalida) ──

	private abrirDialogo(cups?: CupsViewModel): void {
		this.energiaService.getCatalogos().subscribe((catalogos) => {
			this.dialog
				.open(CupsDialogComponent, {
					width: "720px",
					maxWidth: "94vw",
					maxHeight: "88vh",
					autoFocus: false,
					restoreFocus: true,
					data: {
						centroId: this.centroId,
						centroNombre: this.centroNombre,
						catalogos,
						cups,
					},
				})
				.afterClosed()
				.subscribe((result: CupsDialogResult | undefined) => {
					if (result?.changed) {
						this.cargar();
					}
				});
		});
	}

	public crear(): void {
		if (!this.canManage) {return;}
		this.abrirDialogo();
	}

	public editar(c: CupsViewModel): void {
		if (!this.canManage) {return;}
		this.abrirDialogo(c);
	}

	public borrar(c: CupsViewModel): void {
		if (!this.canManage) {return;}
		this.dialogService
			.confirm({
				type: "danger",
				title: this.translate.instant("energia.delete.title"),
				message: this.translate.instant("energia.delete.message", {
					codigo: c.codigo,
					nombre: this.nombrePunto(c),
				}),
			})
			.subscribe((ok) => {
				if (!ok) {return;}
				this.energiaService.deleteCups(c.id).subscribe({
					next: () => this.cargar(),
				});
			});
	}

	public trackCups(_: number, c: CupsViewModel): number {
		return c.id;
	}
}
